import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import api from '../../../../api/client';
import Logo from '../../../../assets/AptiviewLogo.png';
import AptiView from '../../../../assets/AptiviewLogo.png';
import 'bootstrap-icons/font/bootstrap-icons.css';

export default function ResetPasswordConfirm() {
  const navigate = useNavigate();
  const { token } = useParams();
  const location = useLocation();
  const [uid, setUid] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const currentYear = new Date().getFullYear();
  const [passwordStrength, setPasswordStrength] = useState({
    minLength: false,
    hasUppercase: false,
    hasSpecialChar: false,
    hasNumber: false
  });

  // Password strength validation function
  // Extract token from URL parameters
  // const extractedToken = token || new URLSearchParams(location.search).get('token');
  const validatePassword = (pwd) => {
    const minLength = pwd.length >= 12;
    const hasUppercase = /[A-Z]/.test(pwd);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    
    setPasswordStrength({
      minLength,
      hasUppercase,
      hasSpecialChar,
      hasNumber
    });
    
    return minLength && hasUppercase && hasSpecialChar && hasNumber;
  };

  const isPasswordValid = () => {
    return passwordStrength.minLength && 
           passwordStrength.hasUppercase && 
           passwordStrength.hasSpecialChar && 
           passwordStrength.hasNumber;
  };

  // Verify token validity when component loads
  useEffect(() => {
    const queryParamas = new URLSearchParams(location.search);
    const extractedUid = queryParamas.get('uid');
    setUid(extractedUid);

    const verifyToken = async () => {
      if (!token || !extractedUid) {
        setTokenValid(false);
        setError('Invalid reset link. Please request a new password reset.');
        return;
      }

      try {
        // Verify the token is valid (you might need to adjust this endpoint)
        await api.post('/auth/verify-password-reset-token/', {uid: extractedUid, token: token });
        setTokenValid(true);
      } catch (err) {
        console.error('Token verification error:', err);
        setTokenValid(false);
        setError('This reset link has expired or is invalid. Please request a new password reset.');
      }
    };

    verifyToken();
  }, [location.search, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if(!uid){
      setError("User Id is missing from the reset link");
      return;
    }

    if (!isPasswordValid()) {
      setError('Password does not meet the required strength criteria');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/auth/reset-password/', {
        uid : uid,
        token: token,
        new_password: password,
        password_confirm: confirmPassword
      });
      
      setIsSuccess(true);
      setMessage('Your password has been successfully reset! You can now sign in with your new password.');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      const errorMessage = err.response?.data?.detail ||
        err.response?.data?.message ||
        err.response?.data?.password?.[0] ||
        err.response?.data?.token?.[0] ||
        'Failed to reset password. Please try again or request a new reset link.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while verifying token
  if (tokenValid === null) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '2rem',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <i className="bi bi-arrow-clockwise me-2" style={{ animation: 'spin 1s linear infinite', fontSize: '2rem', color: '#0ea5e9' }}></i>
          <p style={{ margin: 0, marginTop: '1rem', color: '#6b7280' }}>Verifying reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
      display: 'flex',
      padding: 0,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated Background Elements */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
        `,
        pointerEvents: 'none'
      }} />

      <div className="row flex-fill w-100 m-0">
        {/* Left Side - AI Interview Theme */}
        <div className="col-md-8 d-none d-md-block p-0 position-relative">
          <div 
            className="h-100 w-100 position-relative d-flex align-items-center justify-content-center"
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              borderRadius: '0 2rem 2rem 0'
            }}
          >
            {/* AI Interview Illustration */}
            <div className="text-center text-white p-5">
              {/* AI Robot Icon */}
              <div style={{
                margin: '0 auto 2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}>
                <img
                  src={AptiView}
                  alt="AptiView Logo"
                  style={{
                     transform: 'scale(0.9)',
                    // width: '200px',
                    // height: '100px',
                  }}
                />
              </div>

              <h1 style={{ 
                fontFamily: 'Poppins, sans-serif',
                fontWeight: '800', 
                fontSize: '3rem',
                marginBottom: '1rem',
                textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
              }}>
                {tokenValid ? 'New Password' : 'Invalid Link'}
              </h1>
              <p style={{ 
                fontSize: '1.3rem', 
                lineHeight: '1.6',
                opacity: '0.95',
                maxWidth: '500px',
                margin: '0 auto',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
              }}>
                {tokenValid 
                  ? 'Create a strong, secure password for your account. Follow our security guidelines for maximum protection.'
                  : 'This reset link has expired or is invalid. Please request a new password reset to continue.'
                }
              </p>
              
              {/* Feature Icons */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '2rem',
                marginTop: '3rem'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 0.5rem',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <i className="bi bi-shield-lock" style={{ fontSize: '1.5rem', color: 'white' }}></i>
                  </div>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Secure</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 0.5rem',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <i className="bi bi-key" style={{ fontSize: '1.5rem', color: 'white' }}></i>
                  </div>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Strong</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 0.5rem',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <i className="bi bi-check-circle" style={{ fontSize: '1.5rem', color: 'white' }}></i>
                  </div>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>Verified</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Reset Password Form */}
        <div className="col-md-4 d-flex align-items-center justify-content-center p-4">
          <div 
            className="w-100 p-5"
            style={{ 
              maxWidth: '450px',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '2rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            {/* Logo and Header */}
            <div className="text-center mb-5">
              <div className="mb-4" style={{ display: 'flex', justifyContent: 'center', }}> 
                <img
                  src={Logo}
                  alt="AptiView Logo"
                  style={{ 
                     transform: 'scale(0.9)',
                    // width: '160px', 
                    // height: '80px',
                    // borderRadius: '16px',
                    // boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    // border: '3px solid rgba(255, 255, 255, 0.5)'
                  }}
                />
              </div>
              <h1 
                style={{ 
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: '800', 
                  fontSize: '2rem',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '0.5rem'
                }}
              >
                {isSuccess ? 'Password Updated!' : tokenValid ? 'Set New Password' : 'Invalid Link'}
              </h1>
              <p style={{ 
                color: '#6b7280', 
                fontSize: '1rem', 
                fontWeight: '500',
                margin: 0
              }}>
                {isSuccess 
                  ? 'Your password has been successfully updated'
                  : tokenValid 
                    ? 'Create a strong password for your account'
                    : 'This reset link is no longer valid'
                }
              </p>
            </div>

            {/* Success Message */}
            {message && (
              <div style={{
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '1.5rem',
                color: '#16a34a',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'flex-start',
                animation: 'fadeInScale 0.4s ease-out',
                lineHeight: '1.5'
              }}>
                <i className="bi bi-check-circle-fill me-2 mt-1" style={{ flexShrink: 0 }}></i>
                <span>{message}</span>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '1.5rem',
                color: '#dc2626',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                animation: 'fadeInScale 0.4s ease-out'
              }}>
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {tokenValid && !isSuccess && (
              <>
                {/* Reset Password Form */}
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label 
                      htmlFor="new-password"
                      style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.875rem'
                      }}
                    >
                      <i className="bi bi-lock me-2"></i>
                      New Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        id="new-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your new password"
                        value={password}
                        onChange={(e) => {
                          const newPassword = e.target.value;
                          setPassword(newPassword);
                          validatePassword(newPassword);
                        }}
                        required
                        disabled={isLoading}
                        style={{
                          width: '100%',
                          padding: '1rem',
                          paddingRight: '3rem',
                          fontSize: '1rem',
                          color: '#111827',
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          transition: 'all 0.15s ease',
                          backdropFilter: 'blur(10px)',
                          fontFamily: 'Inter, sans-serif'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#0ea5e9';
                          e.target.style.boxShadow = '0 0 0 4px rgba(14, 165, 233, 0.1)';
                          e.target.style.background = 'white';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = 'none';
                          e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        style={{
                          position: 'absolute',
                          right: '1rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: '#6b7280',
                          cursor: 'pointer',
                          fontSize: '1.1rem',
                          padding: '0.25rem'
                        }}
                      >
                        <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                      </button>
                    </div>

                    {/* Password Strength Indicator */}
                    {password && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <small style={{ color: '#6b7280', fontWeight: 600 }}>Password Requirements:</small>
                        <div style={{ marginTop: '0.5rem' }}>
                          <div style={{ 
                            fontSize: '0.8rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem',
                            color: passwordStrength.minLength ? '#16a34a' : '#dc2626',
                            marginBottom: '0.25rem'
                          }}>
                            <i className={`bi ${passwordStrength.minLength ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                            Minimum 12 characters
                          </div>
                          <div style={{ 
                            fontSize: '0.8rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem',
                            color: passwordStrength.hasUppercase ? '#16a34a' : '#dc2626',
                            marginBottom: '0.25rem'
                          }}>
                            <i className={`bi ${passwordStrength.hasUppercase ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                            At least one uppercase letter
                          </div>
                          <div style={{ 
                            fontSize: '0.8rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem',
                            color: passwordStrength.hasSpecialChar ? '#16a34a' : '#dc2626',
                            marginBottom: '0.25rem'
                          }}>
                            <i className={`bi ${passwordStrength.hasSpecialChar ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                            At least one special character (!@#$%^&*(),.?\":{}|&lt;&gt;)
                          </div>
                          <div style={{ 
                            fontSize: '0.8rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem',
                            color: passwordStrength.hasNumber ? '#16a34a' : '#dc2626'
                          }}>
                            <i className={`bi ${passwordStrength.hasNumber ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                            At least one number
                          </div>
                        </div>
                        
                        {/* Overall strength indicator */}
                        <div style={{ marginTop: '0.75rem' }}>
                          <div style={{ 
                            background: '#e5e7eb', 
                            height: '4px', 
                            borderRadius: '2px',
                            overflow: 'hidden'
                          }}>
                            <div 
                              style={{ 
                                background: isPasswordValid() ? '#16a34a' : '#f59e0b',
                                height: '100%',
                                width: `${(Object.values(passwordStrength).filter(Boolean).length / 4) * 100}%`,
                                transition: 'all 0.3s ease'
                              }}
                            ></div>
                          </div>
                          <small style={{ 
                            color: isPasswordValid() ? '#16a34a' : '#f59e0b', 
                            fontWeight: 600,
                            fontSize: '0.8rem'
                          }}>
                            {isPasswordValid() ? 'Strong Password ✓' : 'Password Strength: ' + Object.values(passwordStrength).filter(Boolean).length + '/4'}
                          </small>
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label 
                      htmlFor="confirm-password"
                      style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.875rem'
                      }}
                    >
                      <i className="bi bi-lock-fill me-2"></i>
                      Confirm New Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        id="confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm your new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        style={{
                          width: '100%',
                          padding: '1rem',
                          paddingRight: '3rem',
                          fontSize: '1rem',
                          color: '#111827',
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: `2px solid ${confirmPassword && password !== confirmPassword ? '#dc2626' : '#e5e7eb'}`,
                          borderRadius: '12px',
                          transition: 'all 0.15s ease',
                          backdropFilter: 'blur(10px)',
                          fontFamily: 'Inter, sans-serif'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#0ea5e9';
                          e.target.style.boxShadow = '0 0 0 4px rgba(14, 165, 233, 0.1)';
                          e.target.style.background = 'white';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = confirmPassword && password !== confirmPassword ? '#dc2626' : '#e5e7eb';
                          e.target.style.boxShadow = 'none';
                          e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        style={{
                          position: 'absolute',
                          right: '1rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: '#6b7280',
                          cursor: 'pointer',
                          fontSize: '1.1rem',
                          padding: '0.25rem'
                        }}
                      >
                        <i className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                      </button>
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                      <small style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                        Passwords do not match
                      </small>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !isPasswordValid() || password !== confirmPassword}
                    style={{ 
                      width: '100%',
                      padding: '1rem 2rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      fontFamily: 'Poppins, sans-serif',
                      borderRadius: '12px',
                      border: 'none',
                      background: isLoading || !isPasswordValid() || password !== confirmPassword 
                        ? '#9ca3af' 
                        : 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)',
                      color: 'white',
                      cursor: isLoading || !isPasswordValid() || password !== confirmPassword ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s ease',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      marginTop: '1rem',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      if (!isLoading && isPasswordValid() && password === confirmPassword) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isLoading && isPasswordValid() && password === confirmPassword) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                  >
                    {isLoading ? (
                      <>
                        <i className="bi bi-arrow-clockwise me-2" style={{ animation: 'spin 1s linear infinite' }}></i>
                        Updating Password...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-shield-check me-2"></i>
                        Update Password
                      </>
                    )}
                  </button>
                </form>
              </>
            )}

            {/* Back to Login Link */}
            <div className="text-center mt-4 mb-4">
              <p style={{ color: '#6b7280', fontSize: '0.95rem', margin: 0 }}>
                {isSuccess ? (
                  <>
                    Redirecting to login in 3 seconds...{' '}
                    <Link 
                      to="/login" 
                      style={{ 
                        color: '#0ea5e9',
                        textDecoration: 'none',
                        fontWeight: '600',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0369a1'}
                      onMouseLeave={(e) => e.target.style.color = '#0ea5e9'}
                    >
                      Sign In Now
                    </Link>
                  </>
                ) : (
                  <>
                    Back to{' '}
                    <Link 
                      to="/login" 
                      style={{ 
                        color: '#0ea5e9',
                        textDecoration: 'none',
                        fontWeight: '600',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0369a1'}
                      onMouseLeave={(e) => e.target.style.color = '#0ea5e9'}
                    >
                      Sign In
                    </Link>
                  </>
                )}
              </p>
            </div>

            {!tokenValid && (
              <div className="text-center mt-4">
                <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                  Need to reset your password?{' '}
                  <Link
                    to="/reset-password"
                    style={{
                      color: '#0ea5e9',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '0.875rem'
                    }}
                  >
                    Request new reset link
                  </Link>
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="text-center mt-4">
              <p style={{ color: '#9ca3af', fontSize: '0.8rem', margin: 0 }}>
                 © {currentYear} AptiView. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}